import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import Logger from 'core/utils/Logger'
import {
  useRegisterWidgetTours,
  useWidgetTourContext,
  WidgetTourConfig,
} from 'partner360/components/widgetTour'

import { fetchWidgetTours, WidgetTourRaw } from '../api/fetchWidgetTours'

const parseTourConfigs = (rawTours: WidgetTourRaw[]): WidgetTourConfig[] =>
  rawTours.flatMap((rawTour) => {
    try {
      const parsed = JSON.parse(rawTour.tourConfig) as WidgetTourConfig[] | WidgetTourConfig

      return Array.isArray(parsed) ? parsed : [parsed]
    } catch (error: any) {
      Logger.error(
        `Fail to parse tour config for widget: ${rawTour.widgetId}: error: ${error.message}`
      )
      return []
    }
  })

interface IUseWidgetTourOptions {
  isReady?: boolean
  isAutoStartAllowed?: boolean
}

export const useWidgetTour = (
  widgetId: string,
  fallbackTours: WidgetTourConfig[],
  { isReady = true, isAutoStartAllowed = true }: IUseWidgetTourOptions = {}
) => {
  const [tours, setTours] = useState<WidgetTourConfig[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const autoStartAttemptedTourIdsRef = useRef(new Set<string>())
  const { activeTour, startTours } = useWidgetTourContext()

  useEffect(() => {
    const abortController = new AbortController()
    let isCancelled = false

    setIsLoading(true)
    setTours([])

    fetchWidgetTours(widgetId, abortController.signal)
      .then((response) => {
        if (!isCancelled) {
          const parsedTours = parseTourConfigs(response?.data ?? [])

          // The backend owns the content once it has records for this widget; the local
          // config is what ships until then. The widgetId filter keeps a config that was
          // copied from another widget from registering under this one.
          setTours(
            parsedTours.length
              ? parsedTours
              : fallbackTours.filter((tour) => tour.widgetId === widgetId)
          )
        }
      })
      .finally(() => {
        if (!isCancelled) {
          setIsLoading(false)
        }
      })

    return () => {
      isCancelled = true
      abortController.abort()
    }
  }, [fallbackTours, widgetId])

  const availableTours = useMemo(
    () => (isReady ? tours.filter((tour) => tour.enabled === true && tour.steps.length > 0) : []),
    [isReady, tours]
  )
  const autoStartTourIds = useMemo(
    () => availableTours.filter((tour) => tour.autoStart === true).map((tour) => tour.id),
    [availableTours]
  )

  useRegisterWidgetTours(availableTours)

  useEffect(() => {
    const currentAutoStartTourIds = new Set(autoStartTourIds)

    autoStartAttemptedTourIdsRef.current.forEach((tourId) => {
      if (!currentAutoStartTourIds.has(tourId)) {
        autoStartAttemptedTourIdsRef.current.delete(tourId)
      }
    })

    if (isLoading || !isAutoStartAllowed) {
      return
    }

    const pendingTourIds = autoStartTourIds.filter(
      (tourId) => !autoStartAttemptedTourIdsRef.current.has(tourId)
    )

    if (!pendingTourIds.length) {
      return
    }

    const acceptedTourIds = startTours(pendingTourIds)
    acceptedTourIds.forEach((tourId) => autoStartAttemptedTourIdsRef.current.add(tourId))
  }, [autoStartTourIds, isAutoStartAllowed, isLoading, startTours])

  const startTour = useCallback(() => {
    startTours(
      availableTours.map((tour) => tour.id),
      { includeSeen: true }
    )
  }, [availableTours, startTours])

  return {
    hasTours: availableTours.length > 0,
    isLoading,
    isTourActive: activeTour?.widgetId === widgetId,
    startTour,
  }
}
